"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Maximize2, Minimize2, Minus, Plus, RotateCcw } from "lucide-react";

/**
 * A Mermaid figure, themed to the page rather than to Mermaid's defaults, and
 * zoomable, because a system diagram at one column wide is a thumbnail.
 *
 * Mermaid is a browser library — it measures text to lay a graph out, so it
 * needs a real DOM and cannot run on the server. The diagram therefore arrives
 * after hydration, and until it does the figure holds its caption and a quiet
 * placeholder of roughly the right height, so the document does not jump as
 * six diagrams land.
 *
 * The import is dynamic for the same reason it is worth doing: Mermaid is a
 * large parser, and it should not sit in the bundle every page pays for. One
 * `import()` inside an effect keeps it on this route and off the homepage.
 *
 * `securityLevel: "strict"` is the default and stays that way. Nothing here is
 * user input — every definition is a string in lib/whitepaper.ts and
 * lib/legal.ts — but a renderer that strips HTML out of labels is the right
 * posture for a library that turns text into markup.
 *
 * If a definition fails to parse, the figure falls back to showing its source.
 * A document that silently drops a diagram is worse than one that shows you
 * the diagram it could not draw.
 */

type Props = {
  label: string;
  caption: string;
  chart: string;
};

/** Mermaid's `themeVariables`, in the page's own palette. */
const THEME = {
  darkMode: true,
  background: "#0c0c13",

  fontFamily: "var(--font-jakarta), ui-sans-serif, system-ui, sans-serif",
  fontSize: "14px",

  // Nodes.
  primaryColor: "#12121c",
  primaryTextColor: "#fafafa",
  primaryBorderColor: "#22e0ff",
  secondaryColor: "#161624",
  secondaryTextColor: "#fafafa",
  secondaryBorderColor: "#8b5cf6",
  tertiaryColor: "#0c0c13",
  tertiaryTextColor: "#a1a1b5",
  tertiaryBorderColor: "#1d1d2b",

  // Edges and labels.
  lineColor: "#5a5a7a",
  textColor: "#a1a1b5",
  edgeLabelBackground: "#0c0c13",

  // State diagrams.
  labelColor: "#fafafa",
  altBackground: "#12121c",

  // Sequence diagrams.
  actorBkg: "#12121c",
  actorBorder: "#22e0ff",
  actorTextColor: "#fafafa",
  actorLineColor: "#2a2a3d",
  signalColor: "#a1a1b5",
  signalTextColor: "#a1a1b5",
  labelBoxBkgColor: "#12121c",
  labelBoxBorderColor: "#8b5cf6",
  labelTextColor: "#fafafa",
  loopTextColor: "#a1a1b5",
  noteBkgColor: "#161624",
  noteTextColor: "#fafafa",
  noteBorderColor: "#8b5cf6",
  activationBkgColor: "#1d1d2b",
  activationBorderColor: "#22e0ff",
  sequenceNumberColor: "#050509",
} as const;

/** Below 1 the diagram is smaller than the column, which is never useful. */
const MIN_SCALE = 1;
const MAX_SCALE = 6;
const STEP = 1.4;

type View = { scale: number; x: number; y: number };
const IDENTITY: View = { scale: 1, x: 0, y: 0 };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export default function Figure({ label, caption, chart }: Props) {
  const reactId = useId();
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState<View>(IDENTITY);
  const [full, setFull] = useState(false);
  // A ref would do for the drag itself, but the transform needs to know
  // during render whether to animate: a pan that eases is a pan that lags.
  const [panning, setPanning] = useState(false);

  const panel = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; px: number; py: number; x: number; y: number } | null>(null);

  useEffect(() => {
    let live = true;

    void (async () => {
      try {
        const mermaid = (await import("mermaid")).default;

        mermaid.initialize({
          startOnLoad: false,
          theme: "base",
          themeVariables: THEME,
          flowchart: { curve: "basis", htmlLabels: true, padding: 14 },
          sequence: { useMaxWidth: true, mirrorActors: false, messageAlign: "center" },
          // Mermaid renders its own error card into the DOM on a parse failure.
          // We would rather catch it and show the source, so turn that off.
          suppressErrorRendering: true,
        });

        // The id has to be a valid CSS identifier: React's useId is full of
        // colons, which are legal in an id attribute but not in a selector.
        const id = `wp-${reactId.replace(/[^a-zA-Z0-9]/g, "")}`;
        const { svg: out } = await mermaid.render(id, chart);

        if (live) setSvg(out);
      } catch {
        if (live) setFailed(true);
      }
    })();

    return () => {
      live = false;
    };
  }, [chart, reactId]);

  /**
   * Zoom about a point, so whatever is under the cursor stays under it.
   *
   * The transform is `translate(x, y) scale(s)` about the viewport's centre,
   * which puts a screen position at `centre + x + s·local`. Holding `local`
   * fixed across a change of scale is one line of algebra, and it is the
   * difference between zooming into a diagram and zooming past it.
   */
  const zoomAbout = useCallback((factor: number, at?: { x: number; y: number }) => {
    setView((v) => {
      const scale = clamp(v.scale * factor, MIN_SCALE, MAX_SCALE);
      if (scale === v.scale) return v;

      const px = at?.x ?? 0;
      const py = at?.y ?? 0;
      const k = scale / v.scale;

      // Back at 1:1 the diagram belongs centred again, whatever the pan was.
      if (scale === 1) return IDENTITY;

      return { scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  }, []);

  /**
   * Wheel zoom is deliberately modified — ctrl or ⌘, which is also what a
   * trackpad pinch sends. An unmodified wheel over a figure has to keep
   * scrolling the document, or a reader is trapped inside a diagram halfway
   * down a long page. `stopPropagation` keeps the modified case away from
   * Lenis, which listens for wheel on the window.
   */
  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();

      const box = viewport.current?.getBoundingClientRect();
      if (!box) return;

      zoomAbout(e.deltaY < 0 ? STEP : 1 / STEP, {
        x: e.clientX - (box.left + box.width / 2),
        y: e.clientY - (box.top + box.height / 2),
      });
    },
    [zoomAbout],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (view.scale === 1 || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, px: e.clientX, py: e.clientY, x: view.x, y: view.y };
    setPanning(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    setView((v) => ({ ...v, x: d.x + (e.clientX - d.px), y: d.y + (e.clientY - d.py) }));
  };

  const endDrag = (e: React.PointerEvent) => {
    if (drag.current?.id !== e.pointerId) return;
    drag.current = null;
    setPanning(false);
  };

  const toggleFull = useCallback(() => {
    const el = panel.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  }, []);

  // Escape and the browser's own fullscreen chrome both leave without telling
  // us, so the button follows the document rather than its own state.
  useEffect(() => {
    const sync = () => {
      const isFull = document.fullscreenElement === panel.current;
      setFull(isFull);
      if (!isFull) setView(IDENTITY);
    };
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const zoomed = view.scale !== 1 || view.x !== 0 || view.y !== 0;

  const tool =
    "grid h-7 w-7 place-items-center rounded-md text-faint transition-colors duration-150 [transition-timing-function:var(--ease-out)] hover:bg-white/[0.07] hover:text-paper disabled:pointer-events-none disabled:opacity-30";

  return (
    <figure className="my-10 first:mt-0">
      {/* The print stylesheet keeps this panel dark on paper rather than
          flattening it: Mermaid colours its own labels inline, and light text
          on a bleached node is unreadable. See app/globals.css. */}
      <div
        ref={panel}
        className={[
          "material material-lit relative print:p-0",
          full ? "grid place-items-center bg-slate-deep" : "rounded-2xl",
        ].join(" ")}
      >
        {svg ? (
          <div className="absolute right-2.5 top-2.5 z-10 flex items-center gap-0.5 rounded-lg border border-hairline bg-ink/70 p-1 backdrop-blur-md print:hidden">
            <button
              type="button"
              onClick={() => zoomAbout(1 / STEP)}
              disabled={view.scale <= MIN_SCALE}
              className={tool}
              aria-label="Zoom out"
              title="Zoom out"
            >
              <Minus size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => zoomAbout(STEP)}
              disabled={view.scale >= MAX_SCALE}
              className={tool}
              aria-label="Zoom in"
              title="Zoom in"
            >
              <Plus size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => setView(IDENTITY)}
              disabled={!zoomed}
              className={tool}
              aria-label="Reset the view"
              title="Reset"
            >
              <RotateCcw size={13} strokeWidth={2} />
            </button>
            <span aria-hidden className="mx-0.5 h-4 w-px bg-hairline" />
            <button
              type="button"
              onClick={toggleFull}
              className={tool}
              aria-label={full ? "Leave fullscreen" : "Open fullscreen"}
              title={full ? "Leave fullscreen" : "Fullscreen"}
            >
              {full ? (
                <Minimize2 size={13} strokeWidth={2} />
              ) : (
                <Maximize2 size={13} strokeWidth={2} />
              )}
            </button>
          </div>
        ) : null}

        <div
          ref={viewport}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => setView(IDENTITY)}
          // Vertical touch drags stay with the page; the rest goes to the
          // diagram, which is what panning a wide graph on a phone needs.
          style={{ touchAction: view.scale === 1 ? "auto" : "pan-y" }}
          className={[
            "w-full overflow-hidden px-4 py-6 sm:px-6 sm:py-8",
            "print:overflow-visible print:px-0 print:py-0",
            full ? "h-screen px-8 py-8" : "",
            view.scale > 1 ? "cursor-grab active:cursor-grabbing" : "",
          ].join(" ")}
        >
          {svg ? (
            <div
              style={{
                transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                transition: panning ? "none" : "transform 220ms var(--ease-out)",
              }}
              className="[&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full"
              // The markup is Mermaid's own output from a definition that
              // ships in this repo, rendered with securityLevel "strict".
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          ) : failed ? (
            <pre className="overflow-x-auto font-mono text-[12.5px] leading-relaxed text-quiet">
              {chart}
            </pre>
          ) : (
            <div
              aria-hidden
              className="h-40 animate-pulse rounded-xl bg-[linear-gradient(100deg,transparent,rgba(255,255,255,0.045),transparent)]"
            />
          )}
        </div>
      </div>

      <figcaption className="copy mt-3 text-[13.5px] text-faint">
        <span className="font-mono text-[12.5px] uppercase tracking-[0.14em] text-cyan-live/80">
          {label}
        </span>
        <span aria-hidden className="mx-2 text-hairline">
          ·
        </span>
        {caption}
      </figcaption>
    </figure>
  );
}
